/* Home dashboard — small shared helpers used by home.tsx and the per-category
 * schedule widgets: the congregation-id accessor, a locale-aware short-date hook,
 * and the compact SchedulePreview list (next few dated rows + a "view more" link). */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ChevronRight } from "react-bootstrap-icons";
import { TFunction } from "i18next";
import { calendarLocale } from "../../helpers/dateHelpers";
import { HGContext } from "../../helpers/globals";
import { Events, EventTypes, ScheduledEvent } from "../../types/scheduling/events";

export const congId = (ctx: React.ContextType<typeof HGContext>) => ctx.globals.cong?.id ?? 0;

/** Events that cancel the midweek meeting entirely (no regular meeting that week) — mirrors the
 * inline memorial/assembly/convention checks in mm_column.tsx. A CO visit only moves the day
 * (handled by mmDate), it doesn't cancel the meeting. There's no equivalent shared constant for
 * midweek on the scheduling side (weekend has `noMeetingEvents` in wm/wm_common.tsx). */
export const noMidweekMeetingEvents = new Set<Events>([Events.mmem, Events.ca, Events.rc]);

/** The same label EventBadge (scheduling/eventAlert.tsx) shows for an event: its translated name,
 * prefixed with the wine glass used for memorial weeks. */
export function eventLabel(t: TFunction, event: ScheduledEvent): string {
  const prefix = event.event === Events.mmem || event.event === Events.wmem ? "🍷 " : "";
  return prefix + (event.event === Events.custom ? event.custom_title : t(EventTypes[event.event]));
}

/** Convenience wrapper so widgets can read the current congregation id directly. */
export function useCongId(): number {
  return congId(useContext(HGContext));
}

/** Locale-aware short date ("Sat, Jul 4") for an ISO (YYYY-MM-DD) string. */
export function useShortDate() {
  const { i18n } = useTranslation();
  return (iso: string, withYear = false) => {
    const d = new Date(iso.length <= 10 ? iso + "T00:00:00" : iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(calendarLocale(i18n.language), {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
    });
  };
}

/** One row in a schedule-preview widget. Provide either a pre-formatted `dateLabel`
 * or an ISO `dateISO` (formatted via useShortDate). `danger` highlights overdue rows. */
export type PreviewRow = {
  key: React.Key;
  dateISO?: string;
  dateLabel?: string;
  primary: string;
  secondary?: string;
  /** Names rendered as individual chips on the primary line (or the secondary line when `primary` is a title). */
  names?: NameChips;
  danger?: boolean;
};

/** A (possibly truncated) list of names; `moreCount` is how many were left out. */
export type NameChips = { names: string[]; moreCount: number };

/** Keep the first `keep` names and count the rest. */
export const chipsFrom = (names: string[], keep = 2): NameChips => ({
  names: names.slice(0, keep),
  moreCount: Math.max(0, names.length - keep),
});

function Chips({ chips }: { chips: NameChips }) {
  return (
    <span className="w-chips">
      {chips.names.map((n, i) => (
        <span className="w-chip" key={i}>
          {n}
        </span>
      ))}
      {chips.moreCount > 0 && <span className="w-chip w-chip--more">+{chips.moreCount}</span>}
    </span>
  );
}

/** Compact "next few dates" list shared by every per-category schedule widget. */
export function SchedulePreview({ rows, to }: { rows: PreviewRow[]; to: string }) {
  const { t } = useTranslation();
  const shortDate = useShortDate();
  if (rows.length === 0) {
    return <div className="w-empty">{t("schedules.assignment.none")}</div>;
  }
  return (
    <div className="w-sched">
      <ul className="w-sched__list">
        {rows.map((r) => (
          <li className={"w-sched__row" + (r.danger ? " is-danger" : "")} key={r.key}>
            <span className="w-sched__date">{r.dateLabel ?? (r.dateISO ? shortDate(r.dateISO) : "")}</span>
            <div className="w-sched__body">
              {r.names && !r.primary ? (
                <Chips chips={r.names} />
              ) : (
                <span className="w-sched__primary">{r.primary || "—"}</span>
              )}
              {r.names && r.primary ? (
                <Chips chips={r.names} />
              ) : (
                r.secondary && <span className="w-sched__secondary">{r.secondary}</span>
              )}
            </div>
          </li>
        ))}
      </ul>
      <Link className="w-sched__more" to={to}>
        {t("general.more")} <ChevronRight size={12} />
      </Link>
    </div>
  );
}
